import type {
  ApiInventoryRowResponse,
  ApiLiveInventoryResponse,
  ApiOpeningBalanceResponse,
  ApiOpeningBalanceItemResponse,
  InventoryItem,
  InventorySummary,
  LiveInventoryResponse,
  OpeningBalanceItem,
} from "@/app/types/inventory.types";

function mapStatusIdToBarLevelStatus(
  statusId: ApiInventoryRowResponse["inventory_status_id"],
): InventoryItem["bar_level_status"] {
  if (statusId === 1) return "above_level";
  if (statusId === 2) return "at_level";
  if (statusId === 3) return "below_level";
  return "out_of_stock";
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    if (normalized.length === 0) return 0;
    return Number(normalized);
  }
  return Number(value ?? 0);
}

function mapInventoryRow(row: ApiInventoryRowResponse): InventoryItem {
  const stockBalance =
    typeof row.stock === "object" && row.stock !== null
      ? toNumber(row.stock.balance ?? 0)
      : toNumber(row.stock ?? 0);
  const averageCost =
    typeof row.stock === "object" && row.stock !== null
      ? toNumber(row.stock.average_cost ?? 0)
      : toNumber(row.base_acc ?? 0);
  const totalValue =
    typeof row.stock === "object" && row.stock !== null
      ? toNumber(row.stock.total_value ?? 0)
      : toNumber(row.stock ?? 0);
  const lastMovementType =
    typeof row.last_movement === "object" && row.last_movement !== null
      ? row.last_movement.type
      : row.last_movement ?? "";
  const lastMovementDate =
    typeof row.last_movement === "object" && row.last_movement !== null
      ? row.last_movement.last_movement_date ?? ""
      : row.last_movement ?? "";
  const lastMovementQty =
    typeof row.last_movement === "object" && row.last_movement !== null
      ? Number(row.last_movement.total_qty ?? 0)
      : undefined;
  const lastMovementSrc =
    typeof row.last_movement === "object" && row.last_movement !== null
      ? row.last_movement.source
      : undefined;
  

  return {
    id: String(row.id),
    item_name: row.name,
    sku: row.sku,
    category: row.category?.name ?? "-",
    subcategory: row.subcategory?.name ?? "-",
    current_qty: Number.isFinite(stockBalance) ? stockBalance : 0,
    base_unit: row.base_uom?.short_name ?? row.base_uom?.name ?? "",
    base_acc: Number.isFinite(averageCost) ? averageCost : 0,
    stock_value: Number.isFinite(totalValue) ? totalValue : 0,
    last_movement: lastMovementType,
    last_movement_date: lastMovementDate,
    last_movement_qty: Number.isFinite(lastMovementQty ?? NaN)
      ? lastMovementQty
      : undefined,
    source: lastMovementSrc,
    par_level: row.par_level ?? 0,
    bar_level_status: mapStatusIdToBarLevelStatus(row.inventory_status_id),
  };
}

function mapInventorySummary(
  responseData?: ApiLiveInventoryResponse,
): InventorySummary {
  const counters = responseData?.counters;
  return {
    total_items: counters?.total_items ?? 0,
    above_level: counters?.above_level_count ?? 0,
    at_level: counters?.at_level_count ?? 0,
    below_level: counters?.below_count ?? 0,
    out_of_stock: counters?.out_of_stock ?? 0,
    total_stock_value: Number(
      String(counters?.total_stock_value ?? 0).replace(/,/g, ""),
    ),
  };
}

export function mapLiveInventoryResponse(
  responseData?: ApiLiveInventoryResponse,
): LiveInventoryResponse {
  return {
    data: responseData?.data?.data?.map(mapInventoryRow) ?? [],
    opening_balance_draft: responseData?.opening_balance_draft?.items ?? null,
    summary: mapInventorySummary(responseData),
    pagination: {
      page: responseData?.data?.current_page ?? 1,
      limit: responseData?.data?.per_page ?? 10,
      total: responseData?.data?.total ?? 0,
      totalPages: responseData?.data?.total_pages ?? 1,
    },
  };
}

export function mapOpeningBalanceResponse(
  responseData?: ApiOpeningBalanceResponse,
): OpeningBalanceItem[] {
  if (!responseData?.view_open_balance) return [];
  return (responseData?.view_open_balance ?? []).map(mapOpeningBalanceItem);
}

function mapOpeningBalanceItem(
  item: ApiOpeningBalanceItemResponse,
): OpeningBalanceItem {
  const totalQtyBase = toNumber(item.stock?.balance ?? 0);
  const openingStockValue = toNumber(item.stock?.total_value ?? 0);
  const baseAcc = toNumber(item.stock?.average_cost ?? 0);
  const baseEntryQty = toNumber(item.by_base_unit?.entered_base_qty ?? 0);
  const baseEntryUnitCost = toNumber(item.by_base_unit?.unit_cost ?? 0);

  return {
    id: String(item.id),
    sku: item.sku,
    item_name: item.name,
    total_qty_base: Number.isFinite(totalQtyBase) ? totalQtyBase : 0,
    base_unit: item.base_uom?.short_name ?? item.base_uom?.name ?? "",
    base_acc: Number.isFinite(baseAcc) ? baseAcc : 0,
    opening_stock_value: Number.isFinite(openingStockValue) ? openingStockValue : 0,
    base_unit_entry: {
      qty: Number.isFinite(baseEntryQty) ? baseEntryQty : 0,
      unit_cost: Number.isFinite(baseEntryUnitCost) ? baseEntryUnitCost : 0,
    },
    purchase_unit_entries: (item.transaction_details ?? []).map((detail) => ({
      id: String(detail.id),
      variationId: detail.variation?.id ?? detail.id,
      entered_qty: toNumber(detail.qty ?? 0),
      purchase_cost: toNumber(detail.cost_per_variation ?? 0),
      purchase_uom: detail.variation?.purchase_uom?.name ?? "-",
      pack_size: toNumber(detail.variation?.pack_size ?? 0),
      total_base_qty: toNumber(detail.total_base_unit ?? 0),
      base_unit_cost: toNumber(detail.cost_per_unit ?? 0),
    })),
  };
}
